import { proxy, type Snapshot } from 'valtio/vanilla'
import type { ExternalComment, TicketDataResponse } from '@zendesk/zqa-services/tickets'
import { getSharedState } from '../valtio'

// Ticket read-model shared across consumers. This state is the SOURCE OF TRUTH
// for `ticket`, `ticketLoading`, `animateHighlightState` and `loadingTranslation` —
// useTicketContent (Vue) reads and writes it directly via valtioBridge, no
// mirroring. `translatedConversation` is the exception: it's still mirrored in
// from useReviewSidebar's Vue-owned state.
//
// Types moved verbatim from app `modules/conversations/types.ts` (their one
// home is now here; the app file re-exports them so existing imports are
// unchanged). Move, not refactor.

export interface TicketField {
  externalId: string
  name: string
  value: string
}

export interface ExternalCommentExt extends ExternalComment {
  id?: number | string
  hasReviews?: boolean
  highlighted: boolean
  showTime?: boolean
  hasComments?: boolean
  youReviewed?: boolean
  firstMessageInGroup?: boolean
}

export type ExternalCommentSnapshot = Snapshot<ExternalCommentExt>

export interface TicketData
  extends Omit<TicketDataResponse, 'paymentTokenId' | 'externalComments' | 'externalFields' | 'externalTags'> {
  externalFields: TicketField[]
  paymentTokenId: number
  externalComments: ExternalCommentExt[]
  externalTags: string[]
}

export type TicketSnapshot = Snapshot<TicketData>

export interface TicketContentState {
  // {} when no ticket is loaded — mirrors useTicketContent's default state.
  ticket: TicketData
  ticketLoading: boolean
  // Header-action visual flags (from useTicketContent state + sidebar metadata).
  animateHighlightState: boolean
  loadingTranslation: boolean
  translatedConversation: boolean
}

export const ticketContentState = getSharedState('ticket-content', () =>
  proxy<TicketContentState>({
    ticket: {} as TicketData,
    ticketLoading: true,
    animateHighlightState: false,
    loadingTranslation: false,
    translatedConversation: false,
  }),
)

// Pure subject sanitizer, moved verbatim from app `utils/sanitize.ts` (its one
// home is now here; the app file re-exports it). Strips inline tags and decodes
// the one HTML entity the subject may carry.
export const sanitizeTicketSubject = (title: string) =>
  title.replace(/(<p[^>]+?>|<p>|<\/p>|<a[^>]+?>|<a>|<\/a>|<span[^>]+?>|<span>|<\/span>)/gim, '').replace(/&#39;/g, `'`)
